<script setup lang="ts">
import type { UploadFile, UploadProps } from 'ant-design-vue'
import type {
  RcFile,
  UploadRequestOption,
} from 'ant-design-vue/es/vc-upload/interface'

import type {
  UploaderUploadedFile,
  UploaderUploadHandler,
  UploaderUploadResult,
} from './types'

import { computed, onUnmounted, ref, watch } from 'vue'

import { downloadFileFromUrl } from '@vben/utils'

import { DownloadOutlined, UploadOutlined } from '@ant-design/icons-vue'
import { message, Upload } from 'ant-design-vue'

import { ossApi, requestClient } from '#/api'

import { getClipboardFiles, matchesAccept } from './paste'

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    accept?: string
    acceptErrorMessage?: string
    active?: boolean
    disabled?: boolean
    drag?: boolean
    dragDescription?: string
    dragHintText?: string
    fileType?: Numeric
    maxCount?: number
    maxSizeMB?: number
    multiple?: boolean
    paste?: boolean
    showUploadList?: UploadProps['showUploadList']
    templateUrl?: string
    type?: 'button'
    upload?: UploaderUploadHandler
  }>(),
  {
    accept: undefined,
    acceptErrorMessage: undefined,
    active: true,
    disabled: false,
    drag: false,
    dragDescription: undefined,
    dragHintText: undefined,
    fileType: undefined,
    maxCount: 1,
    maxSizeMB: undefined,
    multiple: false,
    paste: true,
    showUploadList: true,
    templateUrl: '',
    type: 'button',
    upload: undefined,
  },
)

const uploadedFiles = defineModel<UploaderUploadedFile[]>('uploadedFiles', {
  default: () => [],
})
const uploading = defineModel<boolean>('uploading', { default: false })

const fileList = ref<UploadFile[]>([])

const isUploading = computed(() =>
  fileList.value.some((file) => file.status === 'uploading'),
)
const isDisabled = computed(() => props.disabled || !props.active)
const isMaxCountReached = computed(
  () => fileList.value.length >= props.maxCount,
)
const resolvedDragHintText = computed(
  () =>
    props.dragHintText ??
    (props.paste
      ? '点击或拖拽或粘贴文件到此区域上传'
      : '点击或拖拽文件到此区域上传'),
)
const resolvedDragDescription = computed(() => props.dragDescription ?? '')

function isSameFile(currentFile: File, targetFile: File) {
  return (
    currentFile === targetFile ||
    (currentFile.name === targetFile.name &&
      currentFile.size === targetFile.size &&
      currentFile.type === targetFile.type &&
      currentFile.lastModified === targetFile.lastModified)
  )
}

function isValidFileCount(file: File, selectedFiles?: File[]) {
  if (fileList.value.length >= props.maxCount) {
    message.error(`最多上传 ${props.maxCount} 个文件`)
    return false
  }

  if (!selectedFiles?.length) {
    return true
  }

  const fileIndex = selectedFiles.findIndex((selectedFile) =>
    isSameFile(selectedFile, file),
  )
  const remainingCount = props.maxCount - fileList.value.length
  if (fileIndex >= remainingCount) {
    message.error(`最多上传 ${props.maxCount} 个文件`)
    return false
  }

  return true
}

function isValidFile(file: File, selectedFiles?: File[]) {
  if (!matchesAccept(file, props.accept)) {
    message.error(props.acceptErrorMessage ?? '文件格式不符合要求')
    return false
  }

  if (
    props.maxSizeMB !== undefined &&
    file.size > props.maxSizeMB * 1024 * 1024
  ) {
    message.error(`单个文件不能超过 ${props.maxSizeMB}MB`)
    return false
  }

  return isValidFileCount(file, selectedFiles)
}

function beforeUpload(file: File, selectedFiles?: File[]) {
  return isValidFile(file, selectedFiles) ? true : Upload.LIST_IGNORE
}

async function uploadToOss(file: File): Promise<UploaderUploadResult> {
  if (props.fileType === undefined) {
    throw new Error('请提供 fileType 或 upload 上传方法')
  }

  const token = await ossApi.getUploadToken(props.fileType)
  const key = token.file_prefix + file.name
  const formData = new FormData()
  formData.append('success_action_status', '200')
  formData.append('policy', token.policy)
  formData.append('x-oss-signature', token.signature)
  formData.append('x-oss-signature-version', token.x_oss_signature_version)
  formData.append('x-oss-credential', token.x_oss_credential)
  formData.append('x-oss-date', token.x_oss_date)
  formData.append('key', key)
  formData.append('x-oss-security-token', token.security_token)
  formData.append('file', file)

  await requestClient.post<unknown>('', formData, {
    baseURL: token.host,
    headers: { 'Content-Type': 'multipart/form-data' },
    responseReturn: 'raw',
  })

  return { key, url: `${token.host}/${key}` }
}

async function uploadFile(file: File) {
  return props.upload ? props.upload(file) : uploadToOss(file)
}

function recordUploadedFile(
  file: File,
  uid: string,
  result: UploaderUploadResult,
) {
  uploadedFiles.value = [
    ...(uploadedFiles.value ?? []).filter((item) => item.uid !== uid),
    { fileName: file.name, result, uid },
  ]
}

function toUploadError(error: unknown) {
  return error instanceof Error ? error : new Error('上传失败')
}

function isRcFile(file: unknown): file is RcFile {
  return file instanceof File && 'uid' in file && typeof file.uid === 'string'
}

async function customRequest(options: UploadRequestOption<unknown>) {
  if (!isRcFile(options.file)) {
    const error = new Error('无法读取待上传文件')
    options.onError?.(error)
    message.error(error.message)
    return
  }

  const file = options.file
  try {
    const result = await uploadFile(file)
    recordUploadedFile(file, file.uid, result)
    options.onSuccess?.(result)
  } catch (error: unknown) {
    options.onError?.(toUploadError(error))
    message.error(`文件「${file.name}」上传失败`)
  }
}

async function uploadPastedFile(file: File) {
  const uid = `paste-${Date.now()}-${Math.random().toString(36).slice(2)}`
  fileList.value = [
    ...fileList.value,
    {
      name: file.name,
      status: 'uploading',
      uid,
    },
  ]

  try {
    const result = await uploadFile(file)
    recordUploadedFile(file, uid, result)
  } catch (error: unknown) {
    fileList.value = fileList.value.map((item) =>
      item.uid === uid
        ? { ...item, error: toUploadError(error), status: 'error' }
        : item,
    )
    message.error(`文件「${file.name}」上传失败`)
  }
}

async function handlePaste(event: ClipboardEvent) {
  if (
    event.defaultPrevented ||
    !props.paste ||
    isDisabled.value ||
    isUploading.value ||
    isMaxCountReached.value
  ) {
    return
  }

  const pastedFiles = getClipboardFiles(event.clipboardData, props.accept)
  if (pastedFiles.length === 0) {
    return
  }

  event.preventDefault()
  const files = props.multiple ? pastedFiles : pastedFiles.slice(0, 1)
  for (const file of files) {
    if (!isValidFile(file)) {
      break
    }
    await uploadPastedFile(file)
  }
}

function handleChange(info: { fileList: UploadFile[] }) {
  fileList.value = info.fileList
}

function handleRemove(file: UploadFile) {
  if (file.status === 'uploading') {
    message.warning('文件上传中，请稍候')
    return false
  }

  fileList.value = fileList.value.filter((item) => item.uid !== file.uid)

  uploadedFiles.value = (uploadedFiles.value ?? []).filter(
    (item) => item.uid !== file.uid,
  )

  return true
}

function downloadTemplate() {
  downloadFileFromUrl({ fileName: '模板', source: props.templateUrl })
}

const pasteEventListener: EventListener = (event) => {
  void handlePaste(event as ClipboardEvent)
}

watch(
  [() => props.paste, () => props.active],
  ([shouldPaste, isActive]) => {
    if (typeof document === 'undefined') {
      return
    }

    document.removeEventListener('paste', pasteEventListener)
    if (shouldPaste && isActive) {
      document.addEventListener('paste', pasteEventListener)
    }
  },
  { immediate: true },
)

watch(
  uploadedFiles,
  (files) => {
    const nextFiles = files ?? []
    if (nextFiles.length === 0) {
      fileList.value = []
      return
    }

    const currentFiles = new Map(fileList.value.map((file) => [file.uid, file]))
    const completedFiles = nextFiles.map<UploadFile>((file) => ({
      ...currentFiles.get(file.uid),
      name: file.fileName,
      response: file.result,
      status: 'done',
      uid: file.uid,
      url: file.result.url,
    }))
    const completedFileUids = new Set(nextFiles.map((file) => file.uid))
    const unfinishedFiles = fileList.value.filter(
      (file) => file.status !== 'done' && !completedFileUids.has(file.uid),
    )
    fileList.value = [...unfinishedFiles, ...completedFiles]
  },
  { deep: true },
)

watch(
  isUploading,
  (value) => {
    uploading.value = value
  },
  { immediate: true },
)

onUnmounted(() => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('paste', pasteEventListener)
  }
})
</script>

<template>
  <component
    :is="props.drag ? Upload.Dragger : Upload"
    :accept="props.accept"
    :before-upload="beforeUpload"
    :custom-request="customRequest"
    :disabled="isDisabled"
    :file-list="fileList"
    :max-count="props.maxCount"
    :multiple="props.multiple"
    :open-file-dialog-on-click="!isDisabled && !isMaxCountReached"
    :show-upload-list="props.showUploadList"
    class="common-uploader w-full"
    v-bind="$attrs"
    @change="handleChange"
    @remove="handleRemove"
  >
    <template v-if="props.drag">
      <div
        class="flex min-h-[168px] flex-col items-center justify-center px-4 py-6"
      >
        <UploadOutlined class="text-primary text-3xl" />
        <div class="text-foreground mt-3 text-base">
          {{ resolvedDragHintText }}
        </div>
        <div
          v-if="resolvedDragDescription"
          class="text-muted-foreground mt-2 text-sm"
        >
          {{ resolvedDragDescription }}
        </div>
      </div>
    </template>
    <template v-else>
      <div class="flex w-full gap-2">
        <AButton
          class="flex-1"
          :disabled="isDisabled || isMaxCountReached"
          type="primary"
        >
          <UploadOutlined />选择文件
        </AButton>
        <AButton v-if="props.templateUrl" @click="downloadTemplate">
          <DownloadOutlined />下载模板
        </AButton>
      </div>
    </template>
  </component>
</template>

<style scoped>
.common-uploader
  :deep(
    .ant-upload-list-text
      .ant-upload-list-item
      .ant-upload-list-item-actions
      .ant-upload-list-item-action
  ) {
  opacity: 1;
}
</style>
